'use client';

import './lightbox.scss';

import {
  Close as RdxClose,
  Content as RdxContent,
  Description as RdxDescription,
  Overlay as RdxOverlay,
  Portal as RdxPortal,
  Root as RdxRoot,
  Title as RdxTitle,
  Trigger as RdxTrigger,
} from '@radix-ui/react-dialog';
import Fade from 'embla-carousel-fade';
import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { trackClick } from '@sola/cookies/components/datalayer.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselLazyLoadImage,
  CarouselLazyLoadVideo,
  CarouselNext,
  CarouselPrevious,
  CarouselProgress,
} from '../carousel/carousel';
import { LightboxButtons } from './lightbox-buttons';

export interface LightboxProps {
  items: FlexibleContentProps[];
  startIndex?: number;
  children?: ReactNode;
}

export const LightboxTrigger = ({
  className,
  children,
  stopPropagation = false,
  ariaLabel,
  onClick,
}: {
  className?: string;
  children?: ReactNode;
  stopPropagation?: boolean;
  ariaLabel?: string;
  onClick?: (e: React.MouseEvent) => void;
}) => {
  const { t } = useTranslation();

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    trackClick(t('core.actions.open'), undefined, className, 'LightboxTrigger');
    onClick?.(e);
  };

  return (
    <RdxTrigger asChild onClick={handleClick} className={className}>
      {children ? (
        <button type="button" aria-label={ariaLabel}>
          {children}
        </button>
      ) : (
        <ButtonStyle size="md">
          <button type="button" aria-label={ariaLabel}>
            {t('core.actions.open')}
          </button>
        </ButtonStyle>
      )}
    </RdxTrigger>
  );
};

export const Lightbox = ({
  items,
  startIndex = 0,
  children,
}: LightboxProps) => {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const handleClose = () => {
    closeButtonRef.current?.click();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    const backgroundClasses = [
      'Lightbox',
      'Lightbox__container',
      'Lightbox__slide',
      'Video',
    ];
    const isBackgroundClick = backgroundClasses.some((className) =>
      target.classList.contains(className),
    );

    if (isBackgroundClick) {
      handleClose();
    }
  };

  return (
    <RdxRoot>
      {children ?? (
        <LightboxTrigger
          className="Lightbox__trigger"
          ariaLabel="open lightbox"
        />
      )}
      <RdxPortal>
        <RdxOverlay className="Lightbox__overlay" onClick={handleClose} />
        <RdxContent className="Lightbox" onClick={handleContentClick}>
          <Carousel
            opts={{
              align: 'start',
              loop: true,
              startIndex,
            }}
            plugins={[Fade()]}
          >
            <div className="Lightbox__container darkBlue-theme pt-comfy-xl md:pt-roomy-sm">
              <div className="Lightbox__toolbar body-lg container mb-comfy-sm md:mb-comfy-xl">
                <div className="Lightbox__toolbar--left">
                  {/* @TODO: Add share functionality */}
                  {/* <div className="Lightbox__share">
                    <button type="button">
                     <ArkIcon iconName="share" size="24" />
                      {t('core.actions.share')}
                    </button>
                  </div> */}
                </div>
                <div className="Lightbox__toolbar--center body-xl">
                  <CarouselProgress />
                </div>
                <div className="Lightbox__toolbar--right">
                  <RdxClose
                    ref={closeButtonRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ArkIcon iconName="x-close" size="24" />
                    {t('core.actions.close')}
                  </RdxClose>
                </div>
              </div>
              {items.length > 1 && (
                <div className="Lightbox__arrows">
                  <CarouselPrevious className="Lightbox__arrow Lightbox__arrow--prev" />
                  <CarouselNext className="Lightbox__arrow Lightbox__arrow--next" />
                </div>
              )}
              <CarouselContent>
                {items.map((slide, index) => (
                  <CarouselItem
                    key={['slide', index, slide.title].join('-')}
                    index={index}
                    className="Lightbox__slide pb-comfy-xl md:pb-roomy-sm"
                    aria-label={
                      slide.title || `${index + 1} of ${items.length}`
                    }
                  >
                    <div className="Lightbox__media mx-auto">
                      {slide.media?.image && !slide.media?.video && (
                        <CarouselLazyLoadImage
                          index={index}
                          className="Lightbox__image"
                          media={slide.media}
                        />
                      )}
                      {slide.media?.video && (
                        <CarouselLazyLoadVideo
                          index={index}
                          className="Lightbox__video"
                          media={slide.media}
                        />
                      )}
                    </div>
                    <div
                      className={cn(
                        'Lightbox__content body-sm lg:body-md w-full max-w-md mx-auto px-comfy-sm pt-comfy-sm md:pt-comfy-xl',
                        !(
                          slide.body ||
                          slide.primaryCTA ||
                          slide.secondaryCTA ||
                          slide.tertiaryCTA
                        ) && 'hidden',
                      )}
                    >
                      <RdxTitle className="Lightbox__title hidden">
                        {slide.title}
                      </RdxTitle>
                      <RdxDescription asChild>
                        <div className="Lightbox__body my-none">
                          {slide.body}
                        </div>
                      </RdxDescription>
                      {(slide.primaryCTA ||
                        slide.secondaryCTA ||
                        slide.tertiaryCTA) && (
                        <div
                          className={cn(
                            'text-center',
                            slide.body && 'mt-comfy-sm',
                          )}
                        >
                          <LightboxButtons
                            {...{
                              primaryCTA: slide.primaryCTA,
                              secondaryCTA: slide.secondaryCTA,
                              tertiaryCTA: slide.tertiaryCTA,
                              buttonSize: 'md',
                            }}
                            inline
                          />
                        </div>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </div>
          </Carousel>
        </RdxContent>
      </RdxPortal>
    </RdxRoot>
  );
};
